import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CallStateEnum } from '@shared/models/call-state.model';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import { withCallState } from '@shared/store-features/call-state-feature';
import axios from 'axios';
import {
  CASTING_INITIAL_STATE,
  CastingState
} from '@modules/casting/store/casting.model.ts';
import { getInternalServers } from '@api/internal-servers-configuration.api.ts';
import {
  getCastAdminData,
  createChromecastAssignment,
  deleteChromecastAssignment
} from '@api/casting.api.ts';
import { InternalServerEnum } from '@models/internal-servers-configuration.model.ts';
import { isElectronApp } from '@shared/hooks/use-is-electron.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useCastingStore = create<CastingState>()(
  devtools(
    (set, getState) => ({
      ...CASTING_INITIAL_STATE,
      ...withCallState(getState),

      getCastData: async () => {
        try {
          set({ callState: CallStateEnum.Loading });

          const isElectron = isElectronApp();

          // Check if internal servers are configured
          let internalServers: any[] = [];
          try {
            internalServers = await getInternalServers();
          } catch (error) {
            // If we can't get internal servers and we're not in Electron, navigate to download page
            if (!isElectron) {
              set({ callState: CallStateEnum.Error });
              // Return early - the component will handle navigation
              return;
            }
          }

          const castGateway = internalServers.find(
            (server) => server.name === InternalServerEnum.CastGateway
          );

          // If no cast gateway is configured and we're not in Electron, navigate to download page
          if (!castGateway && !isElectron) {
            set({ callState: CallStateEnum.Error });
            // Return early - the component will handle navigation
            return;
          }

          const url = castGateway
            ? `http://${castGateway.ipAddress}:${castGateway.port}`
            : 'http://localhost:4008';

          const castData = await getCastAdminData(url);

          set({
            data: castData,
            castGatewayUrl: url,
            lastRefresh: new Date(),
            callState: CallStateEnum.Loaded
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.message ||
              'Failed to fetch cast service data';
            showSnackbar(message, SnackbarType.Error);
          } else {
            showSnackbar('Failed to fetch cast service data', SnackbarType.Error);
          }
        }
      },

      refreshCastData: async () => {
        try {
          const { castGatewayUrl } = getState();
          const castData = await getCastAdminData(castGatewayUrl);

          set({
            data: castData,
            lastRefresh: new Date()
          });
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.message ||
              'Failed to refresh cast service data';
            showSnackbar(message, SnackbarType.Error);
          } else {
            showSnackbar(
              'Failed to refresh cast service data',
              SnackbarType.Error
            );
          }
        }
      },

      assignChromecast: async (data) => {
        try {
          set({ assignmentLoading: true });
          const { castGatewayUrl } = getState();

          await createChromecastAssignment(castGatewayUrl, data);
          showSnackbar('Chromecast assigned successfully', SnackbarType.Success);

          // Refresh data to get updated assignments
          await getState().refreshCastData();

          set({ assignmentLoading: false });
          return true;
        } catch (error) {
          set({ assignmentLoading: false });
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.message || 'Failed to assign Chromecast';
            showSnackbar(message, SnackbarType.Error);
          } else if (error instanceof Error) {
            showSnackbar(error.message, SnackbarType.Error);
          } else {
            showSnackbar('Failed to assign Chromecast', SnackbarType.Error);
          }
          return false;
        }
      },

      unassignChromecast: async (chromecastUuid) => {
        try {
          set({ assignmentLoading: true });
          const { castGatewayUrl } = getState();

          await deleteChromecastAssignment(castGatewayUrl, chromecastUuid);
          showSnackbar(
            'Chromecast unassigned successfully',
            SnackbarType.Success
          );

          // Refresh data to get updated assignments
          await getState().refreshCastData();

          set({ assignmentLoading: false });
          return true;
        } catch (error) {
          set({ assignmentLoading: false });
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.message || 'Failed to unassign Chromecast';
            showSnackbar(message, SnackbarType.Error);
          } else if (error instanceof Error) {
            showSnackbar(error.message, SnackbarType.Error);
          } else {
            showSnackbar('Failed to unassign Chromecast', SnackbarType.Error);
          }
          return false;
        }
      }
    }),
    { name: 'CastingStore' }
  )
);

export default useCastingStore;
